import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Share2, Menu, MessageSquare, User } from 'lucide-react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { cn } from '../lib/utils';

export type Scope = 'default' | 'pmo' | 'jack' | 'finance';

interface TopBarProps {
    chatTitle: string | null;
    scope?: Scope;
    onScopeChange?: (scope: Scope) => void;
    onMenuClick?: () => void;
    onProfileClick?: () => void;
    className?: string;
}

export const TopBar: React.FC<TopBarProps> = ({
    chatTitle,
    scope = 'default',
    onScopeChange,
    onMenuClick,
    onProfileClick,
    className,
}) => {
    const navigate = useNavigate();

    return (
        <TooltipProvider>
            <div className={cn(
                "sticky top-0 z-10 border-b border-border/50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60",
                className
            )}>
                <div className="flex items-center justify-between h-16 px-6">
                    <div className="flex items-center gap-4 flex-1 min-w-0">
                        {onMenuClick && (
                            <Button
                                variant="ghost"
                                size="icon"
                                className="md:hidden flex-shrink-0"
                                onClick={onMenuClick}
                                aria-label="Menu"
                            >
                                <Menu className="h-5 w-5" />
                            </Button>
                        )}

                        <div className="flex items-center gap-3 flex-1 min-w-0 overflow-hidden">
                            {/* Active Assistant Section - Compact Design */}
                            <div className="hidden sm:flex items-center gap-2.5 flex-shrink-0">
                                <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                                    <User className="h-4 w-4 text-primary" />
                                </div>
                                <div className="flex flex-col min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-bold text-foreground truncate">Umer</span>
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-semibold border border-primary/20">
                                            Product & Engineering
                                        </span>
                                    </div>
                                    <span className="text-xs text-muted-foreground truncate">Head of AI Platform</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-1 flex-shrink-0">
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9"
                                    aria-label="Conversations"
                                    onClick={() => navigate('/conversations')}
                                >
                                    <MessageSquare className="h-4 w-4" />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Conversation History</TooltipContent>
                        </Tooltip>

                        {chatTitle && (
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-9 w-9"
                                        aria-label="Share"
                                    >
                                        <Share2 className="h-4 w-4" />
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>Share</TooltipContent>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </TooltipProvider>
    );
};

